/**
 * 播放器頁 —— ⭐ **清單 → 撳一首歌 → 播放器（上：日記文字／下：播放控制）→ 向左掀 → 歌詞**
 *
 * 產品負責人 2026-09-24（照 QQ 音樂三張截圖）：
 *   「for the player tap, should be the playlist, then click into the song, you see the player,
 *    the top show the diary of that song, can see the words, if too many words, then the diary
 *    (the words) should keep scrolling down slowly, then if need to see the lyrics, then flip over」
 *
 * 結構（`P-90` 拆元件；每個檔 ≤200 行）：
 *   · 呢個檔 ＝ **頁面骨架**（資料：library／playlists；狀態：`view`／`page`／`note`）＋頂欄
 *   · `components/SongList.tsx` ＝ 分類 chips ＋ 逐行歌單
 *   · `components/PlayerDeck.tsx` ＝ 揭頁容器（第 0 頁＝日記＋控制、第 1 頁＝歌詞）
 *   · `components/PlayerModes.tsx` ＝ ♥／🔁／🔀（基本播放功能）
 *
 * ⚠️ 播放狀態唔喺呢頁（住 `PlayerProvider`）→ 離開都繼續播（產品負責人 2026-09-21 指示）。
 * ⚠️ 未有音頻 → **照實講**（唔准靜靜唔動）；清單讀唔到 → **照實報**（唔准當「冇歌」）。
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '@coeldery/empty-state';
import ScreenTopBar from '@coeldery/screen-top-bar';
import { useLibrary, type LibrarySong } from '../hooks/useLibrary';
import { usePlaylists } from '../hooks/usePlaylists';
import { tracksFromSongs } from '../player/playerModel';
import { usePlayer } from '../player/PlayerContext';
import AddToPlaylist from '../components/AddToPlaylist';
import FullPlayer from '../player/FullPlayer';
import SongList from '../components/SongList';

/** 頂欄圓掣（例：返去歌單） */
const CIRCLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '40px',
  minHeight: '40px',
  borderRadius: '999px',
  border: '1.5px solid var(--color-divider)',
  background: 'var(--color-card)',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
  fontSize: '17px',
  cursor: 'pointer',
};

type View = 'list' | 'player';

export default function PlayerPage() {
  const { t } = useTranslation();
  const player = usePlayer();
  const { deviceId, songs, hydrated, error: libraryError } = useLibrary();
  const { playlists, error: playlistsError, songsOf } = usePlaylists();
  /** `null` ＝ 〔全部歌〕；有值 ＝ 揀咗嘅清單 */
  const [activeId, setActiveId] = useState<string | null>(null);
  /** `null` ＝ **未載入**（⚠️ 唔可以當「清單係空」） */
  const [list, setList] = useState<LibrarySong[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  /** ⭐ 清單 ⇄ 播放器（產品負責人：「the playlist, then click into the song, you see the player」） */
  const [view, setView] = useState<View>('list');
  const [note, setNote] = useState<string | null>(null);
  const [playlistSongId, setPlaylistSongId] = useState<string | null>(null);

  /** 揀清單 → 攞清單內嘅歌（**加入次序**）；讀唔到 → **照實講** */
  useEffect(() => {
    if (!activeId) {
      setList(null);
      setListError(null);
      return;
    }
    let alive = true;
    setList(null);
    setListError(null);
    void (async () => {
      const res = await songsOf(activeId);
      if (!alive) return;
      if (res.error) {
        setList([]);
        setListError(res.error);
        return;
      }
      setList(res.songs);
    })();
    return () => {
      alive = false;
    };
  }, [activeId, songsOf]);

  const shown = useMemo(() => (activeId ? list ?? [] : songs), [activeId, list, songs]);
  /** 順播用嘅 queue（同畫面次序一致 → 一首接一首） */
  const tracks = useMemo(() => tracksFromSongs(shown, { deviceId }), [deviceId, shown]);
  const loading = activeId !== null && list === null;

  /** 撳一行 → 即刻播 ＋ 入播放器（⚠️ 未有音頻 → 照實講） */
  const openSong = (s: LibrarySong) => {
    const track = tracks.find((x) => x.id === s.id);
    if (!track) {
      setNote(t('song_audio_not_ready'));
      return;
    }
    setNote(null);
    player.play(track, tracks);
    setView('player');
  };

  if (!hydrated) return <p className="page__loading">{t('chat_thinking')}</p>;

  return (
    <div className="page">
      <ScreenTopBar
        title={view === 'list' ? t('tab_player') : t('player_now_playing')}
        left={
          view === 'player' ? (
            <button
              type="button"
              data-testid="player-back-to-list"
              aria-label={t('player_back_to_list')}
              onClick={() => setView('list')}
              style={CIRCLE}
            >
              ⌄
            </button>
          ) : undefined
        }
      />
      <div className="page__scroll" style={{ padding: '12px' }}>
        {libraryError ? (
          <p className="page__error" role="alert">
            {t('songs_list_failed')}（{libraryError}）
          </p>
        ) : null}
        {playlistsError ? (
          <p className="page__error" role="alert">
            {t('playlist_load_failed')}（{playlistsError}）
          </p>
        ) : null}
        {note ? (
          <p className="footnote" role="status" style={{ textAlign: 'center' }}>
            {note}
          </p>
        ) : null}

        {view === 'list' ? (
          <SongList
            songs={songs}
            playlists={playlists}
            activeId={activeId}
            onPickActive={setActiveId}
            onOpen={openSong}
            onAdd={setPlaylistSongId}
            currentId={player.track?.id ?? null}
            playing={player.playing}
            loading={loading}
            listError={listError}
          />
        ) : player.track ? (
          <FullPlayer onClose={() => setView('list')} />
        ) : (
          <EmptyState titleKey="player_empty_title" bodyKey="player_empty_body" />
        )}
      </div>

      {/* ⭐ 0013（`decisions/033`）：〔加入播放清單〕彈窗（全 app 同一個元件） */}
      <AddToPlaylist
        open={playlistSongId !== null}
        songId={playlistSongId}
        onClose={() => setPlaylistSongId(null)}
      />
    </div>
  );
}
